import { IconButton } from '@mui/material';
import { SideCard } from 'components/SideCard';
import jsPDF from 'jspdf';
import type { FlowCurvePointInterface } from './FlowCurve';
import { FlowCurve } from './FlowCurve';
import { PSD_CALC_MODE } from '../types';
import { useEditFlowProperties } from '../useEditFlowProperties';
import PrintIcon from '@mui/icons-material/Print';
import { useRef } from 'react';
import type { FlowPropertiesProps } from '../withFlowProperties';
import { withFlowProperties } from '../withFlowProperties';

type Props = FlowPropertiesProps & {
  pxxPoints: FlowCurvePointInterface[] | undefined;
  setShowCurve: (show: boolean) => void;
};

const FlowSideCard = ({
  id,
  activeUnit,
  flowProperties,
  pxxPoints,
  setShowCurve,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { outputPorts, inputPorts, currentPort, isStorage, setValues } =
    useEditFlowProperties(id, activeUnit, flowProperties);

  const {
    psdCalcMode,
    pxxOne,
    pxxTwo,
    pxxThree,
    fractionOne,
    fractionTwo,
    activePortIndex,
  } = flowProperties;

  return (
    <SideCard
      expandValue={true}
      title={`Flow curve`}
      closeCard={() => setShowCurve(false)}
      menu={
        <IconButton
          size='small'
          onClick={() => {
            const doc = new jsPDF({ orientation: 'portrait' });
            const myCanvas = canvasRef.current;
            const ctx = myCanvas?.toDataURL();
            if (ctx) doc.addImage(ctx, 'PNG', 10, 10, 150, 120);
            doc.save('flow-properties.pdf');
          }}
        >
          <PrintIcon />
        </IconButton>
      }
    >
      <FlowCurve
        activePortIndex={activePortIndex}
        isStorage={isStorage}
        outputPorts={outputPorts}
        inputPorts={inputPorts}
        activeDatasetIndex={activePortIndex}
        pxxPoints={pxxPoints}
        canvasRef={canvasRef}
        lowerLimit={fractionOne}
        upperLimit={fractionTwo}
        psdCalcMode={psdCalcMode}
        setLimits={(lower, upper) =>
          setValues({
            fractionOne: lower,
            fractionTwo: upper,
          })
        }
        setPsdCalcMode={(val: PSD_CALC_MODE) => {
          const tonCovertFactor = currentPort?.outputFlow?.totalMass;

          const getPsdPercent = (value?: number) => {
            if (!value || !tonCovertFactor) {
              return;
            }
            if (val === PSD_CALC_MODE.PERCENT) {
              return Math.round(((value * 100) / tonCovertFactor) * 100) / 100;
            } else if (val === PSD_CALC_MODE.MASS) {
              return Math.round((value / 100) * tonCovertFactor * 100) / 100;
            }
          };

          setValues({
            psdCalcMode: val,
            pxxOne: getPsdPercent(pxxOne),
            pxxTwo: getPsdPercent(pxxTwo),
            pxxThree: getPsdPercent(pxxThree),
          });
        }}
        ticks={
          activeUnit.unitInfo.inputFlow
            ? activeUnit.unitInfo.inputFlow.size
            : activeUnit.flowProperties.size
        }
      />
    </SideCard>
  );
};

export default withFlowProperties(FlowSideCard);
